import Button, { Color, Size } from 'lib/Button';
import cx from 'classnames';
import plusSvg from 'assets/icons/plus.svg';
import { useToggle } from 'customHooks/useToggle';
import { useOutsideClick } from 'customHooks/useOutsideClick';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import { CodeNameModel } from 'components/common/types';
import {
  FilterAdminPerson,
  FilterAdvertiser,
  FilterAgency,
  FilterBrand,
  FilterSalesPerson,
  FilterSalesTeam,
  FilterSpecialist,
} from 'components/common/FilterArea/Filters';
import FilterProductCategoryLookup from 'components/common/FilterArea/Filters/FilterProductCategoryLookup';
import FilterTextInput from 'components/common/FilterArea/Filters/FilterTextInput/FilterTextInput';
import { AdvancedFilter } from '../Filters.types';
import { AdvancedFiltersProps, isTextFilterKey } from './AdvancedFilters.types';
import { AdvancedFiltersPanel } from './AdvancedFiltersPanel';
import { useFilterPanelOffset } from './useFilterPanelOffset';
import FilterDate from '../FilterDate';

const FilterWrapper = ({ children }: { children: JSX.Element }): JSX.Element => (
  <div className="w-44 shrink-0">{children}</div>
);

const AdvancedFilters = ({ changeFilters, clearAllButton, filters }: AdvancedFiltersProps): React.ReactElement => {
  const dropdownRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);
  const [textSearch, setTextSearch] = useState('');

  const { isOpen, toggle, close } = useToggle(false);
  const { menuRef, panelRef, offsetTop } = useFilterPanelOffset(isOpen, activeFilterKey);

  const {
    onLoadLookup,
    onLoadSalesTeam,
    onLoadSalesPerson,
    onLoadAdminPerson,
    onAssetLookup,
    onLoadBrands,
    onLoadProductCategory,
  } = useLoadLookup();

  const applyTextFilter = useCallback(
    (key: string, value: string): void => {
      if (value) {
        changeFilters({ [key]: value });

        return;
      }

      changeFilters({ [key]: undefined });
    },
    [changeFilters],
  );

  const handleClose = useCallback((): void => {
    if (isTextFilterKey(activeFilterKey)) {
      applyTextFilter(activeFilterKey, textSearch);
    }

    close();
    setActiveFilterKey(null);
    setTextSearch('');
  }, [activeFilterKey, applyTextFilter, close, textSearch]);

  useOutsideClick(dropdownRef, handleClose);

  const handleAddFilterClick = (): void => {
    if (isOpen) {
      handleClose();

      return;
    }

    toggle();
  };

  const advancedFilters: AdvancedFilter[] = useMemo(
    () => [
      {
        key: 'salesTeam',
        label: 'Sales team',
        placeholder: 'Search sales team',
        loadData: onLoadSalesTeam,
      },
      {
        key: 'salesPerson',
        label: 'Sales person',
        placeholder: 'Search sales person',
        loadData: onLoadSalesPerson,
      },
      {
        key: 'adminPerson',
        label: 'Admin person',
        placeholder: 'Search admin person',
        loadData: onLoadAdminPerson,
      },
      {
        key: 'advertiser',
        label: 'Advertiser',
        placeholder: 'Search advertiser',
        loadData: onAssetLookup,
      },
      {
        key: 'brand',
        label: 'Brand',
        placeholder: 'Search brand',
        loadData: onLoadBrands,
      },
      {
        key: 'productCategory',
        label: 'Product category',
        placeholder: 'Search product category',
        loadData: onLoadProductCategory,
      },
      {
        key: 'agency',
        label: 'Agency',
        placeholder: 'Search agency',
        loadData: onLoadLookup,
      },
      {
        key: 'specialist',
        label: 'Specialist',
        placeholder: 'Search specialist',
        loadData: onLoadLookup,
      },
      {
        key: 'updatedBy',
        label: 'Updated by',
        placeholder: 'Search updated by',
      },
      {
        key: 'createdBy',
        label: 'Created by',
        placeholder: 'Search created by',
      },
      {
        key: 'createdAt',
        label: 'Created date',
      },
      {
        key: 'dropDate',
        label: 'Drop date',
      },
    ],
    [
      onLoadSalesTeam,
      onLoadSalesPerson,
      onLoadAdminPerson,
      onAssetLookup,
      onLoadBrands,
      onLoadProductCategory,
      onLoadLookup,
    ],
  );

  const activeFilter = useMemo(
    () => advancedFilters.find((filter) => filter.key === activeFilterKey) ?? null,
    [activeFilterKey, advancedFilters],
  );

  const handleLookupSelect = (value: CodeNameModel): void => {
    if (!activeFilterKey) return;

    changeFilters({ [activeFilterKey]: value });
    handleClose();
  };

  const activateFilter = (key: string): void => {
    setActiveFilterKey(key);

    if (isTextFilterKey(key)) {
      setTextSearch(filters[key] ?? '');

      return;
    }

    setTextSearch('');
  };

  useEffect(() => {
    if (!isOpen || !isTextFilterKey(activeFilterKey)) return undefined;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter') return;

      event.preventDefault();
      handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeFilterKey, handleClose, isOpen]);

  return (
    <div className="flex items-start gap-4 w-full min-w-0">
      <div ref={dropdownRef} className="relative shrink-0">
        <Button
          svg={plusSvg}
          label="Add filter"
          onClick={handleAddFilterClick}
          size={Size.LARGE}
          color={Color.BLACK_AND_WHITE}
          classNames="py-2.5"
        />
        {isOpen && (
          <div ref={menuRef} className="absolute z-50 bg-transparent mt-1">
            <div className="flex items-start gap-1 ">
              <div
                data-filter-list
                className="w-52 bg-white border border-neutral-200 rounded-md shadow-dropdownMenu overflow-y-auto"
              >
                {advancedFilters.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    data-filter-key={filter.key}
                    className={cx(
                      'w-full px-4 py-3 flex items-center text-left body-base hover:bg-neutral-200 hover:text-primary-950',
                      activeFilterKey === filter.key && 'bg-neutral-200 text-primary-950',
                    )}
                    onClick={() => activateFilter(filter.key)}
                  >
                    <span>{filter.label}</span>
                  </button>
                ))}
              </div>
              <AdvancedFiltersPanel
                activeFilterKey={activeFilterKey}
                activeFilter={activeFilter}
                filters={filters}
                textSearch={textSearch}
                offsetTop={offsetTop}
                panelRef={panelRef}
                onTextSearchChange={setTextSearch}
                onDateChange={(dateKey, value) => {
                  changeFilters({ [dateKey]: value });
                  handleClose();
                }}
                onLookupSelect={handleLookupSelect}
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-4 items-center flex-1 min-w-0">
        {filters.salesTeam && (
          <FilterWrapper>
            <FilterSalesTeam changeFilters={changeFilters} salesTeam={filters.salesTeam} />
          </FilterWrapper>
        )}
        {filters.salesPerson && (
          <FilterWrapper>
            <FilterSalesPerson changeFilters={changeFilters} salesPerson={filters.salesPerson} />
          </FilterWrapper>
        )}
        {filters.adminPerson && (
          <FilterWrapper>
            <FilterAdminPerson changeFilters={changeFilters} adminPerson={filters.adminPerson} />
          </FilterWrapper>
        )}
        {filters.advertiser && (
          <FilterWrapper>
            <FilterAdvertiser changeFilters={changeFilters} selectedAdvertiser={filters.advertiser} />
          </FilterWrapper>
        )}
        {filters.brand && (
          <FilterWrapper>
            <FilterBrand changeFilters={changeFilters} brand={filters.brand} />
          </FilterWrapper>
        )}
        {filters.productCategory && (
          <FilterWrapper>
            <FilterProductCategoryLookup changeFilters={changeFilters} productCategory={filters.productCategory} />
          </FilterWrapper>
        )}
        {filters.agency && (
          <FilterWrapper>
            <FilterAgency changeFilters={changeFilters} agency={filters.agency} />
          </FilterWrapper>
        )}
        {filters.specialist && (
          <FilterWrapper>
            <FilterSpecialist changeFilters={changeFilters} specialist={filters.specialist} />
          </FilterWrapper>
        )}
        {filters.updatedBy && (
          <FilterWrapper>
            <FilterTextInput
              changeFilters={changeFilters}
              keyName="updatedBy"
              value={filters.updatedBy}
              name="updated-by"
              placeholderText="Search updated by"
            />
          </FilterWrapper>
        )}
        {filters.createdBy && (
          <FilterWrapper>
            <FilterTextInput
              changeFilters={changeFilters}
              keyName="createdBy"
              value={filters.createdBy}
              name="created-by"
              placeholderText="Search created by"
            />
          </FilterWrapper>
        )}
        {filters.createdAt && (
          <FilterWrapper>
            <FilterDate
              changeFilters={changeFilters}
              keyName="createdAt"
              selectedDate={filters.createdAt}
              placeholder="Creation date"
              disabledDays={[{ after: new Date() }]}
            />
          </FilterWrapper>
        )}
        {filters.dropDate && (
          <FilterWrapper>
            <FilterDate
              changeFilters={changeFilters}
              keyName="dropDate"
              selectedDate={filters.dropDate}
              placeholder="Drop date"
            />
          </FilterWrapper>
        )}
      </div>
      {clearAllButton && <div className="shrink-0">{clearAllButton}</div>}
    </div>
  );
};

export default AdvancedFilters;
